import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/db6bhp7qhq9pk5mecs50";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    if (!email || !message) {
      setStatus("error");
      setFormError("请填写邮箱与反馈内容后再提交。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请稍后重试或更换反馈内容。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
        <i className="ri-feedback-line text-lg text-primary-500"></i>
        用户反馈
      </h3>
      <p className="mt-1.5 text-sm text-foreground-500">
        发现错误字幕、想申请某部影片的字幕，或对本站有建议？欢迎告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="feedback_type"
          defaultValue="字幕纠错"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
        >
          <option>字幕纠错</option>
          <option>资源申请</option>
          <option>功能建议</option>
          <option>合作交流</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你的反馈（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        className="contact-alt-hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-wait disabled:opacity-70"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            正在提交
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交反馈
          </>
        )}
      </button>

      {status === "success" && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-xs text-secondary-900">
          <i className="ri-checkbox-circle-line text-sm"></i>
          <span>感谢你的反馈，我们已收到并会尽快处理！</span>
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs text-accent-900">
          <i className="ri-error-warning-line text-sm"></i>
          <span>{formError || "提交失败，请稍后重试。"}</span>
        </div>
      )}
    </form>
  );
}